<script lang="ts">
	import Layout from "@components/layout/Layout.svelte";
	import type { EmblaCarouselType } from "embla-carousel";
	import Autoplay from "embla-carousel-autoplay";
	import chevron from "@core/assets/icons/chevron-arrow.svg?raw";
	import { CategoryGroup } from "@core/schema/CategoryGroup.js";
	import { inquiryId } from "@core/schema/ids/inquiryId.js";
	import { getAboutPath } from "@core/schema/paths/getAboutPath.js";
	import { getInquiryPath } from "@core/schema/paths/getInquiryPath.js";
	import type { CategoryReasonItem } from "@core/schema/zod/categoryReasonsSchema.js";
	import { localizePathFromData } from "@core/utils/localizePathFromData.js";
	import reasonsPlaceholder from "../assets/final_photos/DSC_0013.jpg?webp&w=620&imagetools";
	import ContactForm from "../components/ContactForm.svelte";
	import DeafultTwoColsWithImage from "../components/DeafultTwoColsWithImage.svelte";
	import GalleryCarousel from "../components/GalleryCarousel.svelte";
	import ReferencesCarousel from "../components/ReferencesCarousel.svelte";
	import WhyItem from "../components/WhyItem.svelte";
	import Button from "../components/common/Button.svelte";
	import Carousel from "../components/common/Carousel.svelte";
	import CkEditorContent from "../components/common/CkEditorContent.svelte";
	import Formatted from "../components/common/Formatted.svelte";
	import Icon from "../components/common/Icon.svelte";
	import IconMedia from "../components/common/IconMedia.svelte";
	import Link from "../components/common/Link.svelte";
	import Media from "../components/common/Media.svelte";
	import StickySection from "../components/common/StickySection.svelte";
	import TextWithIcon from "../components/common/TextWithIcon.svelte";
	import { ButtonVariant } from "../components/common/getButtonClass.js";
	import Breadcrumbs from "../components/layout/Breadcrumbs.svelte";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import { truncateText } from "../utils/truncateText.js";
	import type { CategoryProps } from "./CategoryProps.js";
	import translations from "./default.translations.json" with { type: "json" };

	const { tenant, breadcrumbs } = getGlobalTemplateProps();
	const { category, productionAndServicesCategories }: CategoryProps = $props();

	const inquiryPathMapWithParameter = `${getInquiryPath(tenant)}?service=${encodeURIComponent(category.nameForListing)}#${inquiryId[tenant.locale]}`;

	const translate = getTranslate(translations);

	const [firstContent, ...restContents] = category.contents;

	// Ensure products is always an array to avoid 'possibly undefined' in template
	const products = Array.isArray(category.products) ? category.products : [];

	let showAllProducts = $state(false);

	const textForCard = category.buttonTextSuffix ? category.buttonTextSuffix : "";

	const seconds = 8;
	let reasonsCarouselApi: EmblaCarouselType | undefined = $state();
	let currentIndex = $state(0);

	const reasonsItems = $derived(category.reasons?.items ?? []);

	// eslint-disable-next-line new-cap
	const reasonsAutoplay = Autoplay({ delay: seconds * 1000, stopOnInteraction: true, stopOnMouseEnter: false });

	function goToReasonSlide(index: number): void {
		reasonsCarouselApi?.scrollTo(index);
	}

	$effect(() => {
		if (reasonsCarouselApi) {
			const api = reasonsCarouselApi;

			const onSelect = (): void => {
				currentIndex = api.selectedScrollSnap();
			};

			api.on("select", onSelect);

			return (): void => {
				api.off("select", onSelect);
			};
		}

		return undefined;
	});

	// Counter for section background alternation (0 = white, 1 = gray, 2 = white, etc.)
	let sectionCounter = 0;

	const getSectionIndex = (): number => {
		return sectionCounter++;
	};
	const hasGrayBackground = (sectionIndex: number): boolean => sectionIndex % 2 === 1;

	const hasReversedLayout = (sectionIndex: number): boolean => sectionIndex % 2 === 1;
</script>

<Layout>
	<Breadcrumbs {breadcrumbs} currentPageTitle={category.nameForListing} />
	<section class="full-width grid-container pb-8 sm:pb-10 md:pb-12 xl:pb-[5.5rem]">
		<div class="container-center flex flex-col xl:flex-row xl:justify-between gap-8 sm:gap-10 xl:gap-[8.25rem]">
			<h1 class="text-zms-blue font-light tracking-[-4%] text-left xl:w-[32rem] xl:shrink-0">
				<Formatted text={category.name} />
			</h1>
			<div class="flex flex-col gap-6 sm:gap-8 xl:gap-10">
				<p class="font-normal text-sm sm:text-base xl:text-lg/[1.4] text-optical-black text-left">
					<Formatted text={category.description} />
				</p>
				<div>
					<Link href={inquiryPathMapWithParameter} variant={ButtonVariant.OutlinedWithIcon}
						><TextWithIcon>{translate("inquire")} <span class="uppercase">{textForCard}</span></TextWithIcon></Link
					>
				</div>
			</div>
		</div>
	</section>

	{#if products.length > 0}
		{@const productsSectionIndex = getSectionIndex()}
		<section
			class="full-width grid-container {hasGrayBackground(productsSectionIndex)
				? 'bg-zms-grey'
				: ''} py-8 sm:py-12 md:py-16 lg:py-20"
		>
			<div
				class="container-center flex items-start md:items-center gap-8 sm:gap-10 md:gap-10 lg:gap-12 flex-col mb-8 sm:mb-12 md:mb-16 lg:mb-24"
			>
				{#if category.productsTitle}
					<h2 class="lg:w-3/5 text-left md:text-center">
						<Formatted text={category.productsTitle} />
					</h2>
				{/if}
				{#if category.productsDescription}
					<p
						class="font-normal text-sm sm:text-base text-left md:text-center lg:w-[70%] lg:text-lg/[1.4] text-optical-black"
					>
						<Formatted text={category.productsDescription} />
					</p>
				{/if}
			</div>
			<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
				{#each products as product, productIndex (productIndex)}
					{@const isClickable = Boolean(products[productIndex]?.urlSlug && products[productIndex]?.urlSlug.length > 0)}
					<div
						class="bg-white rounded-xl flex flex-col overflow-hidden shadow-sm transition-all duration-300 border border-gray-50 {isClickable
							? 'hover:shadow-lg hover:-translate-y-1 cursor-pointer'
							: ''}"
						class:hidden={productIndex >= 4 && !showAllProducts}
						class:md:block={productIndex >= 4 && !showAllProducts}
					>
						{#if product?.photo}
							<div class="w-full aspect-[3/2] sm:aspect-[4/3] md:aspect-[3/2] flex-shrink-0 overflow-hidden bg-gray-50">
								{#if product.urlSlug && product.urlSlug.length > 0}
									<Link href={product.urlSlug} class="block w-full h-full">
										<Media
											wrapperClass="w-full h-full"
											class="object-cover w-full h-full hover:scale-110 transition-transform duration-500 ease-out"
											height={240}
											width={360}
											alt={product.text}
											source={product.photo}
										/>
									</Link>
								{:else}
									<Media
										wrapperClass="w-full h-full"
										class="object-cover w-full h-full"
										height={240}
										width={360}
										alt={product.text}
										source={product.photo}
									/>
								{/if}
							</div>
						{/if}
						{#if product?.text}
							<div class="p-3 md:p-6 flex-1 flex flex-col justify-start min-h-[5rem] md:min-h-[6rem]">
								{#if product.urlSlug}
									<Link
										href={product.urlSlug}
										class="text-md/[1.2] md:text-lg/[1.2] font-medium text-zms-blue-dark hover:text-zms-blue"
									>
										<Formatted text={product.text} />
									</Link>
								{:else}
									<p class="text-md/[1.2] md:text-lg/[1.2] font-medium text-zms-blue-dark">
										<Formatted text={product.text} />
									</p>
								{/if}
							</div>
						{/if}
					</div>
				{/each}

				{#if products.length > 4 && !showAllProducts}
					<Button
						variant={ButtonVariant.OutlinedWithIconTight}
						onclick={(): void => {
							showAllProducts = true;
						}}
						class="col-span-1 sm:col-span-2 md:col-span-3 xl:col-span-4 block md:hidden"
					>
						{translate("showAll")}
					</Button>
				{/if}
			</div>
		</section>
	{/if}

	{#if category.gallery && category.gallery.photos.length > 0}
		{@const gallerySectionIndex = getSectionIndex()}
		<GalleryCarousel gallery={category.gallery} bgColor={hasGrayBackground(gallerySectionIndex) ? "bg-zms-grey" : ""} />
	{/if}

	{#if category.numberedItemsSection && category.numberedItemsSection.items.length > 0}
		{@const numberedSectionIndex = getSectionIndex()}
		<section class="full-width grid-container {hasGrayBackground(numberedSectionIndex) ? 'bg-zms-grey' : ''}">
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-20">
				<div
					class="flex order-2 lg:order-1 flex-col gap-8 lg:gap-6 {hasReversedLayout(numberedSectionIndex)
						? 'lg:order-2'
						: 'lg:order-1'}"
				>
					{#each category.numberedItemsSection.items as item, index (index)}
						<div
							class="flex flex-col md:gap-16 p-4 md:p-6 rounded-2xl {hasGrayBackground(numberedSectionIndex)
								? 'bg-white'
								: 'bg-zms-grey'}"
						>
							<div class="text-zms-blue text-3xl/[1] font-hubot font-light tracking-[-1%]">{index + 1}</div>
							<div class="flex flex-col gap-6">
								<h3 class="text-zms-blue-dark md:text-[2rem]/[1] text-[1.6rem]/[1.2] font-medium tracking-[-1%]">
									<Formatted text={item.title} />
								</h3>
								<p class="text-optical-black text-[1.125rem]/[1.4] font-normal">
									<Formatted text={item.description} />
								</p>
							</div>
						</div>
					{/each}
				</div>
				<div
					class="flex order-1 lg:order-2 flex-col gap-6 lg:gap-10 lg:text-end text-start lg:items-end items-start {hasReversedLayout(
						numberedSectionIndex,
					)
						? 'lg:order-1'
						: 'lg:order-2'}"
				>
					<StickySection>
						<h2>
							<Formatted text={category.numberedItemsSection.title} />
						</h2>

						<div class="flex flex-col gap-10 md:gap-16 mt-4 md:mt-8">
							<p class="font-normal md:text-lg/[1.4] text-optical-black flex flex-col gap-6 h-full">
								<span><Formatted text={category.numberedItemsSection.description} /></span>
							</p>
							<div>
								<Link href={inquiryPathMapWithParameter} variant={ButtonVariant.OutlinedWithIcon}
									><TextWithIcon>{translate("inquire")} <span class="uppercase">{textForCard}</span></TextWithIcon
									></Link
								>
							</div>
						</div>
					</StickySection>
				</div>
			</div>
		</section>
	{/if}

	{#if firstContent}
		{@const firstContentSectionIndex = getSectionIndex()}
		<section
			class="full-width grid-container {hasGrayBackground(firstContentSectionIndex)
				? 'bg-zms-grey'
				: ''} py-10 md:py-[5.5rem]"
		>
			<div class="container-center flex items-center flex-col gap-11 lg:gap-[5.5rem]">
				{#if firstContent.photo}
					<div
						class="flex flex-col-reverse lg:flex-row lg:justify-between gap-8 lg:gap-[8.25rem] w-full {hasReversedLayout(
							firstContentSectionIndex,
						)
							? 'lg:flex-row-reverse'
							: ''}"
					>
						<Media
							wrapperClass="lg:w-[32rem] h-[16rem] lg:h-[32rem]"
							class="rounded-2xl object-cover w-full h-full"
							height={512}
							width={512}
							alt={firstContent.text}
							source={firstContent.photo}
						/>
						<div class="flex flex-col lg:w-[32rem] gap-8 lg:gap-6 lg:order-2">
							<div>
								<CkEditorContent html={firstContent.text} />
							</div>
							<div class="lg:block hidden">
								<Link href={inquiryPathMapWithParameter} variant={ButtonVariant.OutlinedWithIcon}>
									<TextWithIcon>{translate("inquire")} <span class="uppercase">{textForCard}</span></TextWithIcon>
								</Link>
							</div>
						</div>
					</div>
					<div class="lg:hidden">
						<Link href={inquiryPathMapWithParameter} variant={ButtonVariant.OutlinedWithIcon}>
							<TextWithIcon>{translate("inquire")} <span class="uppercase">{textForCard}</span></TextWithIcon>
						</Link>
					</div>
				{:else}
					<div class="w-full flex justify-center">
						<div
							class="lg:w-[70%]"
							class:text-center={!firstContent.text.includes("<ul>") && !firstContent.text.includes("<ol>")}
						>
							<CkEditorContent html={firstContent.text} />
						</div>
					</div>
				{/if}
			</div>
		</section>
	{/if}

	{#if category.unnumberedItemsSection && category.unnumberedItemsSection.items.length > 0}
		{@const unnumberedSectionIndex = getSectionIndex()}
		<section
			class="full-width grid-container {hasGrayBackground(unnumberedSectionIndex)
				? 'bg-zms-grey'
				: ''} py-8 sm:py-12 md:py-16"
		>
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-20">
				<div class="flex flex-col gap-4 sm:gap-6 lg:gap-10 text-start items-start">
					<StickySection class="py-2 sm:py-4 md:py-6">
						<h2>
							<Formatted text={category.unnumberedItemsSection.title} />
						</h2>
					</StickySection>
				</div>
				<div class="flex flex-col gap-4 sm:gap-6 lg:gap-6">
					{#each category.unnumberedItemsSection.items as item, index (index)}
						<div
							class="flex flex-col gap-4 sm:gap-6 md:gap-8 lg:gap-11 border-t border-zms-blue sm:px-6 py-2 sm:py-8 lg:py-10"
						>
							<h3 class="text-zms-blue-dark text-base sm:text-lg lg:text-[2rem]/[1] font-semibold">
								<Formatted text={item.title} />
							</h3>
							{#if item.description && item.description.length > 0}
								<div class="flex flex-col gap-3 sm:gap-4 md:gap-6 lg:gap-7">
									<div class="flex items-start gap-2">
										<div class="w-1.5 h-1.5 rounded-full bg-zms-optical-black mt-2 shrink-0"></div>
										<p
											class="text-zms-optical-black text-sm sm:text-base md:text-lg lg:text-[1.125rem]/[1.4] font-semibold"
										>
											<Formatted text={item.description} />
										</p>
									</div>
								</div>
							{/if}
						</div>
					{/each}
				</div>
			</div>
		</section>
	{/if}

	{#if category.references && category.references.photos.length > 0}
		{@const referencesSectionIndex = getSectionIndex()}
		<ReferencesCarousel
			references={category.references}
			bgColor={hasGrayBackground(referencesSectionIndex) ? "bg-zms-grey" : ""}
		/>
	{/if}

	{#each restContents as content, index (index)}
		{@const sectionIndex = getSectionIndex()}
		<section
			class="full-width grid-container {hasGrayBackground(sectionIndex) ? 'bg-zms-grey' : ''} py-10 md:py-[5.5rem]"
		>
			<div class="container-center flex items-center flex-col gap-11 lg:gap-[5.5rem]">
				{#if content.photo}
					<div
						class="flex flex-col-reverse lg:flex-row lg:justify-between gap-8 lg:gap-[8.25rem] w-full {hasReversedLayout(
							sectionIndex,
						)
							? 'lg:flex-row-reverse'
							: ''}"
					>
						<Media
							wrapperClass="lg:w-[32rem] h-[16rem] lg:h-[32rem]"
							class="rounded-2xl object-cover w-full h-full"
							height={512}
							width={512}
							alt={content.text}
							source={content.photo}
						/>
						<div class="flex flex-col lg:w-[32rem] gap-8 lg:gap-6 lg:order-1">
							<div>
								<CkEditorContent html={content.text} />
							</div>
							<div class="lg:block hidden">
								<Link href={inquiryPathMapWithParameter} variant={ButtonVariant.OutlinedWithIcon}>
									<TextWithIcon>{translate("inquire")} <span class="uppercase">{textForCard}</span></TextWithIcon>
								</Link>
							</div>
						</div>
					</div>
					<div class="lg:hidden">
						<Link href={inquiryPathMapWithParameter} variant={ButtonVariant.OutlinedWithIcon}>
							<TextWithIcon>{translate("inquire")} <span class="uppercase">{textForCard}</span></TextWithIcon>
						</Link>
					</div>
				{:else}
					<!-- Pokud není obrázek, zobraz text na celou šířku vycentrovaný -->
					<div class="w-full flex justify-center">
						<div
							class="lg:w-[70%]"
							class:text-center={!content.text.includes("<ul>") && !content.text.includes("<ol>")}
						>
							<CkEditorContent html={content.text} />
						</div>
					</div>
				{/if}
			</div>
		</section>
	{/each}

	{#if category.reasons && category.reasons.items.length > 0}
		{@const reasonsSectionIndex = getSectionIndex()}
		<section class="full-width {hasGrayBackground(reasonsSectionIndex) ? 'bg-zms-grey' : ''} grid-container">
			<div class="container-center flex items-center flex-col gap-8 sm:gap-12 md:gap-16 lg:gap-[5.5rem]">
				<h2 class="text-left md:text-center w-full"><Formatted text={category.reasons.title} /></h2>
				<p class="font-normal text-sm sm:text-base xl:text-lg/[1.4] text-optical-black md:text-center w-full">
					<Formatted text={category.reasons.description} />
				</p>
				<div class="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-6 w-full">
					<div class="flex flex-col gap-4 sm:gap-6 md:gap-12 order-2 md:order-1">
						{#each category.reasons.items as item, i (i)}
							<button
								class="text-left outline-none"
								onclick={(): void => {
									goToReasonSlide(i);
								}}
							>
								<WhyItem
									number={i + 1}
									title={item.title}
									subtitle={item.description}
									isActive={i === currentIndex}
									duration={seconds}
								/>
							</button>
						{/each}
					</div>
					<div class="overflow-hidden order-1 md:order-2">
						<Carousel
							bind:api={reasonsCarouselApi}
							items={reasonsItems}
							type="flex"
							plugins={[reasonsAutoplay]}
							options={{
								loop: true,
								align: "center",
								slidesToScroll: 1,
								containScroll: "trimSnaps",
								watchDrag: false,
							}}
						>
							{#snippet item(reasonItem: CategoryReasonItem)}
								<div class="flex-none w-full h-[400px] sm:h-[480px] md:h-[560px] relative">
									{#if reasonItem.photo}
										<Media
											class="w-full h-full object-contain"
											height={560}
											width={584}
											alt={reasonItem.title}
											source={reasonItem.photo}
										/>
									{:else}
										<Media
											class="w-full h-full object-contain"
											height={560}
											width={584}
											alt={reasonItem.title}
											source={{ src: reasonsPlaceholder, type: "image/webp" }}
										/>
									{/if}
								</div>
							{/snippet}
						</Carousel>
					</div>
				</div>
			</div>
			<div class="mt-12 sm:mt-14 md:mt-16 flex w-full justify-start md:justify-center">
				<Link href={getAboutPath(tenant)} variant={ButtonVariant.OutlinedWithIcon}
					><TextWithIcon>Více o nás</TextWithIcon></Link
				>
			</div>
		</section>
	{/if}

	{#if category.parentCategory === CategoryGroup.ProductionAndServices}
		{@const servicesSectionIndex = getSectionIndex()}
		<section
			class="full-width grid-container {hasGrayBackground(servicesSectionIndex)
				? 'bg-zms-grey'
				: ''} py-6 sm:py-8 md:py-12"
		>
			<div class="sm:grid container-center flex flex-col gap-6 grid-cols-2 md:gap-8 lg:gap-20">
				<StickySection>
					<div class="gap-8 lg:gap-10 flex flex-col">
						<h2 class="text-zms-blue font-light tracking-[-4%] text-left">
							<Formatted text={translate("servicesMainHeading")} />
						</h2>
						<div class="flex flex-col gap-4">
							<p
								class="font-normal text-sm sm:text-base md:text-lg/[1.4] text-optical-black flex flex-col gap-3 sm:gap-4 md:gap-6 h-full"
							>
								<Formatted text={translate("servicesDescription")} />
							</p>
							<p
								class="font-normal text-sm sm:text-base md:text-lg/[1.4] text-optical-black flex flex-col gap-3 sm:gap-4 md:gap-6 h-full"
							>
								<Formatted text={translate("servicesContactText")} />
							</p>
						</div>
						<div>
							<Link href={inquiryPathMapWithParameter} variant={ButtonVariant.OutlinedWithIcon}
								><TextWithIcon>{translate("inquire")} <span class="uppercase">{textForCard}</span></TextWithIcon></Link
							>
						</div>
					</div>
				</StickySection>
				<div class="flex flex-col gap-2">
					{#each productionAndServicesCategories as service, index (index)}
						{#if service.id !== category.id}
							<Link
								href={localizePathFromData(service.urlSlug, tenant.locale)}
								class="group bg-white rounded-2xl flex gap-4 p-4 transition-all duration-300 ease-in-out overflow-hidden hover:shadow-md"
								variant={ButtonVariant.Transparent}
							>
								<div class="bg-zms-blue w-24 h-24 shrink-0 rounded-[0.25rem] flex justify-center items-center">
									<IconMedia
										source={service.image}
										alt={service.name}
										class="w-10 h-10 text-zms-blue"
										width={96}
										height={96}
										useCurrentColor={true}
									/>
								</div>
								<div class="flex flex-col justify-center flex-1 min-h-[96px] relative">
									<h3
										class="font-medium text-[1.25rem]/[1.2] text-zms-dark-blue transition-all duration-300 ease-in-out xl:group-hover:transform xl:group-hover:-translate-y-8"
									>
										{service.nameForListing}
									</h3>
									<p
										class="text-sm text-gray-600 xl:block hidden opacity-0 transition-all duration-300 ease-in-out group-hover:opacity-100 absolute top-10"
									>
										{truncateText(service.descriptionForListing, 99)}
									</p>
								</div>
								<div class="justify-end items-center flex">
									<Icon icon={chevron} class="h-6 text-zms-blue" />
								</div>
							</Link>
						{/if}
					{/each}
				</div>
			</div>
		</section>
	{/if}

	{@const finalSectionIndex = getSectionIndex()}
	<DeafultTwoColsWithImage
		bgColor={hasGrayBackground(finalSectionIndex) ? "bg-zms-grey" : ""}
		heading={translate("heading")}
		text={translate("text")}
		buttonText={translate("buttonText")}
	/>

	<ContactForm heading="withText" />
</Layout>
